import { usePortalMessage } from "../../i18n/message.js";
import { portalText, usePortalTranslation, portalLocale } from "../../i18n/portal.js";
/**
 * Confirm a change WeTalk support prepared — `/confirm-operator-action#<token>` (plan-014 F3d, the hand-off from F3a;
 * FR12-OPS-01, FR12-OPS-02, FR12-OPS-05). The page MAIL's `operator_action_confirmation` email links to.
 *
 * Some changes support prepares wait for the person who asked: an account recovery, an authenticator reset, an email
 * change, a funding change. The page reads what the link asks for (the organization, the change in plain words, until
 * when), and only a press of "Confirm" confirms it — opening the link changes nothing. No sign-in is asked for: the
 * link went to the person's verified address, and a recovery is needed exactly when they cannot sign in.
 *
 * After the confirmation: a change that also needs a second WeTalk operator says so (they decide within 7 days);
 * otherwise WeTalk support applies it. Every unusable link — used, expired, never issued — is one page.
 *
 * Drawn with the Organization pages' blocks (`../account-admin/page/organization-page.tsx`) and tokens only.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { ApiError } from "@wetalk/api-client";
import { Button } from "@wetalk/ui";
import { Link } from "@tanstack/react-router";
import { type ReactNode } from "react";

import { apiProblemText } from "../../api/api-error.js";
import { useGateway } from "../../api/provider.js";
import { GLYPH } from "../../shell/glyph.js";
import { Notice } from "../account-admin/page/organization-page.js";
import "./confirm-operator-action-gateway.js";
import type { OperatorActionConfirmedView } from "./confirm-operator-action-gateway.js";

import "../account-admin/page/organization-page.css";

export interface ConfirmOperatorActionScreenProps {
  /** The token from the link's fragment, exactly as it arrived (never logged); empty for none. */
  readonly token: string;
}

function Page(props: { readonly title: string; readonly lede: string; readonly children?: ReactNode }): ReactNode {
  usePortalTranslation("core");
  return (
    <section className="op-page">
      <p className="op-kicker">{portalText("core", "confirm_operator_action.wetalk_support_74a6da")}</p>
      <h1 className="op-title">{props.title}</h1>
      <p className="op-lede">{props.lede}</p>
      {props.children}
    </section>
  );
}

/** "Mon 6 Oct, 14:30" in the reader's own zone — the link's expiry is theirs to read. */
function whenOf(iso: string): string {
  return new Intl.DateTimeFormat(portalLocale() === "en" ? "en-GB" : "el-GR", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }).format(new Date(iso));
}

function Unusable(): ReactNode {
  usePortalTranslation("core");
  return (
    <Page
      title={portalText("core", "mail_landing.this_link_cannot_be_used_fcb489")}
      lede={portalText("core", "confirm_operator_action.it_was_already_used_it_expired_or_it_is_incomplete_nothing_was_ch_b04aed")}
    >
      <div className="op-block">
        <Notice
          tone="neutral"
          glyph={GLYPH.lockSimple}
          title={portalText("core", "confirm_operator_action.your_organization_is_as_it_was_a1c690")}
          text={portalText("core", "confirm_operator_action.a_change_wetalk_support_prepares_never_happens_without_your_confi_840bc5")}
          actions={
            <Link to="/log-in" className="btn btn-secondary">{portalText("core", "auth.log_in_f7c400")}</Link>
          }
        />
      </div>
    </Page>
  );
}

function Confirmed(props: { readonly outcome: OperatorActionConfirmedView }): ReactNode {
  usePortalTranslation("core");
  const { outcome } = props;
  return (
    <Page
      title={portalText("core", "confirm_operator_action.thank_you_you_confirmed_the_change_0326f3")}
      lede={
        outcome.state === "awaiting_approval"
          ? portalText("core", "confirm_operator_action.a_second_person_at_wetalk_now_approves_it_you_will_receive_an_ema_b06f21", { value0: outcome.expiresAt === null ? "" : portalText("core", "confirm_operator_action._by_813a94") })
          : portalText("core", "confirm_operator_action.wetalk_support_now_completes_it_you_will_receive_an_email_when_it_fc02b6")
      }
    >
      <div className="op-block">
        <Notice tone="accent" glyph={GLYPH.checkCircle} title={portalText("core", "confirm_operator_action.confirmed_8cc7ac")} text={outcome.description} />
      </div>
    </Page>
  );
}

export function ConfirmOperatorActionScreen(props: ConfirmOperatorActionScreenProps): ReactNode {
  usePortalTranslation("core");
  const gateway = useGateway();
  const [problem, setProblem] = usePortalMessage();
  const inspect = gateway.inspectOperatorActionConfirmation;
  const confirmIt = gateway.confirmOperatorAction;
  const link = useQuery({
    queryKey: ["operator-action-confirmation", props.token],
    queryFn: () => {
      if (inspect === undefined) throw new Error("unavailable");
      return inspect(props.token);
    },
    retry: false,
    staleTime: 0,
    enabled: props.token !== "" && inspect !== undefined,
  });
  const confirm = useMutation({
    mutationFn: () => {
      if (confirmIt === undefined) throw new Error("unavailable");
      return confirmIt(props.token);
    },
    onError: (raised: Error) => {
      setProblem(() => (raised instanceof ApiError && raised.code === "not_found" ? portalText("core", "confirm_operator_action.this_link_was_used_or_expired_meanwhile_nothing_was_changed_5ff511") : apiProblemText(raised, portalText("core", "confirm_operator_action.the_change_could_not_be_confirmed_f949b9"))));
    },
  });

  if (inspect === undefined || confirmIt === undefined) {
    return (
      <Page title={portalText("core", "mail_landing.nothing_to_confirm_here_49d03f")} lede={portalText("core", "confirm_operator_action.this_console_cannot_receive_confirmations_from_wetalk_support_ope_8e1129")}>
        <div className="op-block" />
      </Page>
    );
  }
  if (props.token === "") return <Unusable />;
  if (confirm.data !== undefined) return <Confirmed outcome={confirm.data} />;
  if (link.isPending) {
    return (
      <Page title={portalText("core", "confirm_operator_action.opening_the_change_f75c4a")} lede={portalText("core", "start.one_moment_b0fb11")}>
        <div className="op-block" />
      </Page>
    );
  }
  if (link.isError) {
    if (link.error instanceof ApiError && link.error.code === "not_found") return <Unusable />;
    return (
      <Page title={portalText("core", "confirm_operator_action.the_link_could_not_be_opened_421c3f")} lede={apiProblemText(link.error, portalText("core", "start.try_the_link_again_in_a_moment_87d514"))}>
        <div className="op-block" />
      </Page>
    );
  }
  const view = link.data;
  return (
    <Page
      title={portalText("core", "confirm_operator_action.confirm_a_change_to_98ed5a", { value0: view.accountName })}
      lede={portalText("core", "confirm_operator_action.wetalk_support_prepared_this_change_with_you_nothing_happens_unle_cdda5a", { value0: whenOf(view.expiresAt) })}
    >
      <div className="op-block">
        <Notice
          tone="accent"
          glyph={GLYPH.shieldCheck}
          title={portalText("core", "confirm_operator_action.what_changes_0ccfcd")}
          text={view.description}
          actions={
            <Button variant="primary" disabled={confirm.isPending} onClick={() => confirm.mutate()}>{portalText("core", "account_admin.confirm_04a212")}</Button>
          }
        />
      </div>
      {view.secondApproverRequired ? (
        <div className="op-block">
          <Notice tone="neutral" glyph={GLYPH.usersThree} title={portalText("core", "confirm_operator_action.a_second_person_approves_it_fd63d0")} text={portalText("core", "confirm_operator_action.after_you_confirm_a_second_person_at_wetalk_approves_the_change_b_c495d5")} />
        </div>
      ) : null}
      {problem === null ? null : (
        <p className="op-toast op-toast-problem" role="status">
          {problem}
        </p>
      )}
      <div className="op-block">
        <Notice tone="neutral" glyph={GLYPH.envelope} title={portalText("core", "mail_landing.did_not_ask_for_this_5b454c")} text={portalText("core", "confirm_operator_action.ignore_this_page_and_the_email_nothing_changes_without_your_confi_c2a733")} />
      </div>
    </Page>
  );
}
